import { format, parse, addDays, differenceInDays, isBefore, isAfter, isSameDay } from "date-fns";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import isSameOrBefore from "dayjs/plugin/isSameOrBefore";

dayjs.extend(utc);
dayjs.extend(duration);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

export class DateTimeUtil {
    static DEFAULT_DATE_FORMAT = "dd/MM/yyyy";
    static DEFAULT_DATETIME_FORMAT = "yyyy-MM-dd HH:mm:ss";

    static DATETIME_FANCY_1 = "MMM D, YYYY hh:mm";
    static DATETIME_FANCY_2 = "MMM D, hh:mm a";
    static MONTH_DATE = "MMM d";
    static DAY_OF_WEEK = "ddd";
    static TIME_FORMAT_1 = "HH:mm a";
    // Formatting dates using date-fns
    static formatDate(
        date: Date | string | null | undefined,
        dateFormat: string = DateTimeUtil.DEFAULT_DATETIME_FORMAT
    ): string {
        if (!date) return "Invalid date";
        return format(new Date(date), dateFormat);
    }

    // Add this method inside DateTimeUtil class
    static formatShortDate(date: Date | string | null | undefined): string {
        if (!date) return "Invalid date";
        return dayjs(date).format("ddd, D MMM"); // Example output: "Mon, 14 Feb"
    }

    // Parsing dates using date-fns
    static parseDate(
        dateString: string | null | undefined,
        dateFormat: string = DateTimeUtil.DEFAULT_DATETIME_FORMAT
    ): Date {
        if (!dateString) return new Date(NaN);
        return parse(dateString, dateFormat, new Date());
    }

    // Adding days using date-fns
    static addDaysToDate(date: Date | string | null | undefined, days: number): Date {
        if (!date) return new Date(NaN);
        return addDays(new Date(date), days);
    }

    // Difference in days using date-fns
    static differenceInDays(
        dateLeft: Date | string | null | undefined,
        dateRight: Date | string | null | undefined
    ): number {
        if (!dateLeft || !dateRight) return NaN;
        return differenceInDays(new Date(dateLeft), new Date(dateRight));
    }

    static isSameDayWithDateFns(
        dateLeft: Date | string | null | undefined,
        dateRight: Date | string | null | undefined
    ): boolean {
        if (!dateLeft || !dateRight) return false;
        return isSameDay(new Date(dateLeft), new Date(dateRight));
    }

    // Check if two dates are the same day using dayjs
    static isSameDayWithDayjs(
        dateLeft: Date | string | null | undefined,
        dateRight: Date | string | null | undefined
    ): boolean {
        if (!dateLeft || !dateRight) return false;
        return dayjs.utc(dateLeft).isSame(dayjs.utc(dateRight), "day");
    }

    // Check if a date is before another date using date-fns
    static isBefore(date: Date | string | null | undefined, dateToCompare: Date | string | null | undefined): boolean {
        if (!date || !dateToCompare) return false;
        return isBefore(new Date(date), new Date(dateToCompare));
    }

    // Check if a date is after another date using date-fns
    static isAfter(date: Date | string | null | undefined, dateToCompare: Date | string | null | undefined): boolean {
        if (!date || !dateToCompare) return false;
        return isAfter(new Date(date), new Date(dateToCompare));
    }

    // Formatting dates using dayjs
    static formatWithDayjs(
        date: Date | string | null | undefined,
        dateFormat: string = DateTimeUtil.DEFAULT_DATETIME_FORMAT
    ): string {
        if (!date) return "Invalid date";
        return dayjs(date).format(dateFormat);
    }

    // Parsing dates using dayjs
    static parseWithDayjs(dateString: string | null | undefined): dayjs.Dayjs {
        return dateString ? dayjs(dateString) : dayjs(null);
    }

    // Adding days using dayjs
    static addDaysWithDayjs(date: Date | string | null | undefined, days: number): dayjs.Dayjs {
        return date ? dayjs(date).add(days, "day") : dayjs(null);
    }

    // Difference in days using dayjs
    static differenceInDaysWithDayjs(
        dateLeft: Date | string | null | undefined,
        dateRight: Date | string | null | undefined
    ): number {
        if (!dateLeft || !dateRight) return NaN;
        return dayjs(dateLeft).diff(dayjs(dateRight), "day");
    }

    // Check if a date is before another date using dayjs
    static isBeforeWithDayjs(
        date: Date | string | null | undefined,
        dateToCompare: Date | string | null | undefined
    ): boolean {
        if (!date || !dateToCompare) return false;
        return dayjs(date).isBefore(dayjs(dateToCompare));
    }

    // Check if a date is after another date using dayjs
    static isAfterWithDayjs(
        date: Date | string | null | undefined,
        dateToCompare: Date | string | null | undefined
    ): boolean {
        if (!date || !dateToCompare) return false;
        return dayjs.utc(date).isAfter(dayjs.utc(dateToCompare));
    }

    // Format date/time range
    static formatDateTimeRange(
        startDateTime: Date | string | null | undefined,
        endDateTime: Date | string | null | undefined,
        invalidMessage: string = "Invalid date",
        includeDate: boolean = true
    ): string {
        const start = dayjs(startDateTime, DateTimeUtil.DEFAULT_DATETIME_FORMAT);
        const end = dayjs(endDateTime, DateTimeUtil.DEFAULT_DATETIME_FORMAT);

        let formattedStart;
        if (includeDate) {
            formattedStart = start.isValid() ? start.format("MMM D h:mmA") : invalidMessage;
        } else {
            formattedStart = start.isValid() ? start.format("h:mmA") : invalidMessage;
        }

        const formattedEnd = end.isValid() ? end.format("h:mmA") : invalidMessage;

        return `${formattedStart} - ${formattedEnd}`;
    }

    static formatDateTimeRangeUtc(
        startDateTime: Date | string | null | undefined,
        endDateTime: Date | string | null | undefined,
        invalidMessage: string = "Invalid date",
        includeDate: boolean = true
    ): string {
        const start = dayjs.utc(startDateTime, DateTimeUtil.DEFAULT_DATETIME_FORMAT);
        const end = dayjs.utc(endDateTime, DateTimeUtil.DEFAULT_DATETIME_FORMAT);

        let formattedStart;
        if (includeDate) {
            formattedStart = start.isValid() ? start.format("MMM D h:mmA") : invalidMessage;
        } else {
            formattedStart = start.isValid() ? start.format("h:mmA") : invalidMessage;
        }

        const formattedEnd = end.isValid() ? end.format("h:mmA") : invalidMessage;

        return `${formattedStart} - ${formattedEnd}`;
    }

    static formatTimeRangeUtc(
        startDateTime: Date | string | null | undefined,
        endDateTime: Date | string | null | undefined,
        invalidMessage: string = "Invalid time"
    ): string {
        const start = dayjs.utc(startDateTime, DateTimeUtil.DEFAULT_DATETIME_FORMAT);
        const end = dayjs.utc(endDateTime, DateTimeUtil.DEFAULT_DATETIME_FORMAT);

        const formattedStart = start.isValid() ? start.format("h:mmA") : invalidMessage;
        const formattedEnd = end.isValid() ? end.format("h:mmA") : invalidMessage;

        return `${formattedStart} - ${formattedEnd}`;
    }

    static formatDateRangeFancy(start: string, end: string, useUTC: boolean = true): string {
        let startDate: dayjs.Dayjs | Date;
        let endDate: dayjs.Dayjs | Date;

        // Try parsing with dayjs first
        if (dayjs(start, DateTimeUtil.DEFAULT_DATETIME_FORMAT, true).isValid()) {
            startDate = useUTC
                ? dayjs.utc(start, DateTimeUtil.DEFAULT_DATETIME_FORMAT)
                : dayjs(start, DateTimeUtil.DEFAULT_DATETIME_FORMAT);
            endDate = useUTC
                ? dayjs.utc(end, DateTimeUtil.DEFAULT_DATETIME_FORMAT)
                : dayjs(end, DateTimeUtil.DEFAULT_DATETIME_FORMAT);
        } else {
            // Fallback to native Date parsing
            startDate = new Date(start);
            endDate = new Date(end);
        }

        // Ensure both dates are valid
        if (
            (startDate instanceof Date && isNaN(startDate.getTime())) ||
            (endDate instanceof Date && isNaN(endDate.getTime()))
        ) {
            return "Invalid date range";
        }

        // Helper function to format correctly based on instance type
        const formatDate = (date: dayjs.Dayjs | Date, formatStr: string): string => {
            if (dayjs.isDayjs(date)) {
                return date.format(formatStr);
            } else {
                return format(date as Date, formatStr);
            }
        };

        const startMonth = formatDate(startDate, "MMM");
        const endMonth = formatDate(endDate, "MMM");

        const startDay = formatDate(startDate, "D");
        const endDay = formatDate(endDate, "D");

        if (startDay === endDay && startMonth === endMonth) {
            return `${startMonth} ${startDay}`;
        }

        return startMonth === endMonth
            ? `${startMonth} ${startDay} - ${endDay}`
            : `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    }

    // Calculate duration between two dates
    static getDuration(start?: string | null, end?: string | null): string {
        if (!start || !end) return "0h 0m";
        const startTime = dayjs(start);
        const endTime = dayjs(end);
        if (!startTime.isValid() || !endTime.isValid()) return "0h 0m";
        const duration = endTime.diff(startTime, "minute");
        const hours = Math.floor(duration / 60);
        const minutes = duration % 60;
        return `${hours}h ${minutes}m`;
    }

    // Get formatted date string like "Aug 2"
    static getFormattedDate(dateString: string | null | undefined, invalidMessage: string = "Invalid date"): string {
        if (!dateString) return invalidMessage;
        const date = dayjs(dateString, DateTimeUtil.DEFAULT_DATETIME_FORMAT);
        return date.isValid() ? date.format("MMM D") : invalidMessage;
    }

    static getFormattedTime(timeString: string | null | undefined, invalidMessage: string = "Invalid time"): string {
        if (!timeString) return invalidMessage;
        const time = dayjs(timeString, DateTimeUtil.DEFAULT_DATETIME_FORMAT);
        return time.isValid() ? time.format("h:mmA") : invalidMessage;
    }

    static getFormattedTimeUtc(timeString: string | null | undefined, invalidMessage: string = "Invalid time"): string {
        if (!timeString) return invalidMessage;
        const time = dayjs(timeString, DateTimeUtil.DEFAULT_DATETIME_FORMAT).utc();
        return time.isValid() ? time.format("h:mmA") : invalidMessage;
    }

    static formatDateToMySQL(date: Date, excludeTime: boolean = false): string {
        const year = date.getFullYear();
        const month = (date.getMonth() + 1).toString().padStart(2, "0");
        const day = date.getDate().toString().padStart(2, "0");
        const hours = date.getHours().toString().padStart(2, "0");
        const minutes = date.getMinutes().toString().padStart(2, "0");
        const seconds = date.getSeconds().toString().padStart(2, "0");

        if (excludeTime) {
            return `${year}-${month}-${day}`;
        }

        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    }

    static formatDayjsToMySQL(date: dayjs.Dayjs): string {
        return date.format("YYYY-MM-DD HH:mm:ss");
    }

    /***
     * Dayjs specific
     */

    // Format a date using dayjs in UTC
    static FormatWithDayjsUtc(
        date: Date | string | null | undefined,
        dateFormat: string = DateTimeUtil.DEFAULT_DATETIME_FORMAT
    ): string {
        if (!date) return "Invalid date";
        return dayjs.utc(date).format(dateFormat);
    }

    static FormatDurationFromStart(
        start: Date | string | null | undefined
    ): string {
        if (!start) return "Invalid start time";

        const now = dayjs.utc();
        const startTime = dayjs.utc(start);
        const diff = now.diff(startTime);

        const dur = dayjs.duration(diff);
        const hours = Math.floor(dur.asHours());
        const minutes = dur.minutes();

        const hoursStr = hours > 0 ? `${hours} hr${hours !== 1 ? "s" : ""}` : "";
        const minutesStr = `${minutes} m`;

        return `${hoursStr}${hoursStr && minutes ? " " : ""}${minutesStr}`;
    }

    // Parse a date using dayjs in UTC
    static ParseWithDayjsUtc(dateString: string | null | undefined): dayjs.Dayjs {
        return dateString ? dayjs.utc(dateString) : dayjs(null);
    }

    // Add days to a date using dayjs in UTC
    static AddDaysWithDayjsUtc(date: Date | string | null | undefined, days: number): dayjs.Dayjs {
        return date ? dayjs.utc(date).add(days, "day") : dayjs(null);
    }

    // Difference in days using dayjs in UTC
    static DifferenceInDaysWithDayjsUtc(
        dateLeft: Date | string | null | undefined,
        dateRight: Date | string | null | undefined
    ): number {
        if (!dateLeft || !dateRight) return NaN;
        return dayjs.utc(dateLeft).diff(dayjs.utc(dateRight), "day");
    }

    // Check if a date is before another date using dayjs in UTC
    static IsBeforeWithDayjsUtc(
        date: Date | string | null | undefined,
        dateToCompare: Date | string | null | undefined
    ): boolean {
        if (!date || !dateToCompare) return false;
        return dayjs.utc(date).isBefore(dayjs.utc(dateToCompare));
    }

    // Check if a date is after another date using dayjs in UTC
    static IsAfterWithDayjsUtc(
        date: Date | string | null | undefined,
        dateToCompare: Date | string | null | undefined
    ): boolean {
        if (!date || !dateToCompare) return false;
        return dayjs.utc(date).isAfter(dayjs.utc(dateToCompare));
    }

    // Check if two dates are the same day using dayjs in UTC
    static IsSameDayWithDayjsUtc(
        dateLeft: Date | string | null | undefined,
        dateRight: Date | string | null | undefined
    ): boolean {
        if (!dateLeft || !dateRight) return false;
        return dayjs.utc(dateLeft).isSame(dayjs.utc(dateRight), "day");
    }

    /**
     * Generates an array of dates between a specified start and end date.
     * Optionally includes or excludes the start and end dates and can ignore the time component.
     *
     * @param start - The start date of the range.
     * @param end - The end date of the range.
     * @param includeBounds - Boolean flag to include or exclude the start and end dates in the result. Defaults to true (includes bounds).
     * @param ignoreTime - Boolean flag to reset the time of each date to midnight (00:00:00). Defaults to true.
     * @returns Array of Date objects representing each day in the range.
     */
    static GetDatesBetween(start: Date, end: Date, includeBounds: boolean = true, ignoreTime: boolean = true): Date[] {
        const dates = []; // Array to hold each date in the range
        const currentDate = new Date(start); // Copy of start date to iterate through the range

        // Reset time to midnight if ignoreTime is true
        if (ignoreTime) {
            currentDate.setHours(0, 0, 0, 0);
            end.setHours(0, 0, 0, 0);
        }

        // Adjust starting point if bounds are not included
        if (!includeBounds) {
            currentDate.setDate(currentDate.getDate() + 1); // Move to the day after the start date
        }

        // Loop through dates until reaching or including the end date based on includeBounds
        while (currentDate <= end) {
            dates.push(new Date(currentDate)); // Add the current date to the array
            currentDate.setDate(currentDate.getDate() + 1); // Move to the next day
        }

        // If bounds are excluded, ensure the end date itself is not included in the result
        if (!includeBounds && dates[dates.length - 1].getTime() === end.getTime()) {
            dates.pop(); // Remove the end date if it was included
        }

        return dates; // Return the array of dates within the specified range
    }

    // Returns the day name from a given date (e.g., Monday, Tuesday)
    static getDayName(date: Date | string | null | undefined): string {
        if (!date) return "Invalid date";
        return dayjs(date).format("dddd");
    }

    // Returns the abbreviated day name from a given date (e.g., Mon, Tue)
    static getAbbreviatedDayName(date: Date | string | null | undefined): string {
        if (!date) return "Invalid date";
        return dayjs(date).format("ddd");
    }

    // Returns the full month name from a given date (e.g., January, February)
    static getMonthName(date: Date | string | null | undefined): string {
        if (!date) return "Invalid date";
        return dayjs(date).format("MMMM");
    }

    // Formats a date in a readable "Day, Date Month Year" format (e.g., Monday, 15 January 2023)
    static formatFullDate(date: Date | string | null | undefined): string {
        if (!date) return "Invalid date";
        return dayjs(date).format("dddd, D MMMM YYYY");
    }

    // Combines day and time into a readable format (e.g., "Monday 2:30 PM")
    static formatDayAndTime(date: Date | string | null | undefined): string {
        if (!date) return "Invalid date";
        return dayjs(date).format("dddd h:mm A");
    }

    // Get time in 24-hour format (e.g., 14:30)
    static getTime24HourFormat(date: Date | string | null | undefined): string {
        if (!date) return "Invalid time";
        return dayjs(date).format("HH:mm");
    }

    static formatDateRange = (start: string, end: string): string => {
        const startDate = new Date(start);
        const endDate = new Date(end);

        const startMonth = format(startDate, "MMM");
        const endMonth = format(endDate, "MMM");

        const startDay = format(startDate, "d");
        const endDay = format(endDate, "d");

        if (startDay === endDay) {
            return `${startMonth} ${startDay}`;
        }

        return startMonth === endMonth
            ? `${startMonth} ${startDay} - ${endDay}`
            : `${startMonth} ${startDay} - ${endMonth} ${endDay}`;
    };

    static formatElegantTime(time: Date | string | null | undefined, invalidMessage: string = "Invalid time"): string {
        if (!time) return invalidMessage;
        const parsed = dayjs.utc(time);
        return parsed.isValid() ? parsed.format("hh:mm A") : invalidMessage;
    }

    static formatElegantTimeRange(
        start: string | null | undefined,
        end: string | null | undefined,
        invalidMessage: string = "N/A"
    ): string {
        if (!start || !end) return invalidMessage;

        const startTime = dayjs.utc(start);
        const endTime = dayjs.utc(end);

        const isStartMidnight = startTime.format("HH:mm:ss") === "00:00:00";
        const isEndMidnight = endTime.format("HH:mm:ss") === "00:00:00";

        // If both are exactly midnight, consider it a full-day leave
        if (isStartMidnight && isEndMidnight) {
            return "Full day";
        }

        if (!startTime.isValid() || !endTime.isValid()) {
            return invalidMessage;
        }

        return `${startTime.format("hh:mm A")} - ${endTime.format("hh:mm A")}`;
    }
}

export default DateTimeUtil;
